import { Map, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { MapPin } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InlineEditor } from './InlineEditor';
import { MAP_DEFAULT_PROPS } from './mapDefaults';
import {
  PlacesAutocomplete,
  extractAddressFromGoogleResult,
  type PlacesAutocompleteResult,
} from './PlacesAutocomplete';
import { SelectInput, type SelectInputOption } from './SelectInput';
import { TextInput } from './TextInput';

/**
 * Subset of an address shape — the editor only touches these fields. Apps
 * can extend with `id`, `addressType`, etc. and pass the wider object
 * through; the editor preserves the rest via the spread in `onChange`.
 *
 * `latitude`/`longitude` are populated by the optional `<PlacesAutocomplete>`
 * search bar (when `enablePlacesSearch` is set). Apps that require valid
 * geocoding gate save on these being non-null.
 */
/** Where an address's coordinates came from — mirrors Directory's `CoordinatesSource`. */
export type AddressCoordinatesSource = 'Google' | 'Manual';

export interface AddressInlineEditorValue {
  /**
   * Single Google-formatted address line (verbatim from Places /
   * Geocoder API). Replaces the previous Address1 + Address2 split
   * which lost parts of the address Google's parser didn't surface
   * (unit numbers, townlands, county names, etc.). City / Postcode /
   * CountryIsoCode stay as searchable sidecars below.
   */
  fullAddress: string;
  city?: string | null;
  postcode?: string | null;
  countryIsoCode: string;
  latitude?: number | null;
  longitude?: number | null;
  /**
   * Google `place_id` — stable identifier for re-fetch + dedup.
   * Captured from the Places picker / Find-on-map result.
   */
  placeId: string;
  /**
   * Where `latitude`/`longitude` came from: `Google` (a Places pick or Find on
   * map) or `Manual` (the operator dragged or tapped the pin). Directory
   * refreshes Google coordinates from the place id before they turn 30 days
   * old — Google's caching limit — and never touches Manual ones, so a pin
   * placed on the gate stays on the gate. Forward it on save.
   */
  coordinatesSource?: AddressCoordinatesSource | null;
  /** Optional name field (used by location/site editors, not org). */
  name?: string;
  /**
   * Optional short reference code — e.g. `HQ`, `WAREHOUSE-01`, `BILL-DUB`.
   * Surfaced via `hasCodeField` so editors that don't track this field
   * (the Organisation main-office form, for instance) keep their slim
   * shape.
   */
  code?: string | null;
}

export interface AddressInlineEditorProps<T extends AddressInlineEditorValue> {
  /** Floating label on the summary line. Defaults to `Address`. */
  label?: string;
  value: T;
  onChange: (next: T) => void;
  /** Country options. When omitted, falls back to a free-text input. */
  countryOptions?: SelectInputOption[];
  /** Show a name field at the top of the expanded panel. */
  hasNameField?: boolean;
  /**
   * Show a code field at the top of the expanded panel — sits above the
   * name field when both are enabled. Used by editors that need a short
   * stable reference alongside the human-readable name (e.g. parties).
   */
  hasCodeField?: boolean;
  /**
   * When `true`, render a Google `<PlacesAutocomplete>` search bar at the top
   * of the expanded panel. Picking a result populates every address field
   * including `latitude`/`longitude`. The host app must provide a maps API
   * provider higher in the tree (see `@my/ui` docs).
   */
  enablePlacesSearch?: boolean;
  /**
   * When `true`, render an embedded Google Map plus a "Find on map" button
   * that geocodes the typed address and verifies the coordinates. The
   * `mapPosition` prop chooses where the map sits relative to the form
   * fields. Requires a maps API provider higher in the tree (same caveat
   * as `enablePlacesSearch`); when no provider is wired, the map panel
   * renders a degraded placeholder rather than crashing.
   */
  showMap?: boolean;
  /**
   * Where to render the map relative to the address fields. `'bottom'`
   * (default) stacks the map below the field grid — matches the legacy
   * single-column editor used by parties / organisations. `'right'`
   * splits the panel into two columns on `md+` screens (form on the
   * left, map on the right), and falls back to a stacked layout on
   * mobile so the map doesn't squeeze the form into illegibility.
   *
   * Has no effect when `showMap` is false.
   */
  mapPosition?: 'bottom' | 'right';
  /**
   * Optional content rendered inside the form column, below the address
   * fields. Used by the Locations editor to drop a service-tags chip row
   * into the panel without baking tag-fetching into `@my/ui` (the host
   * owns the data + toggle logic). Sits in the left column when
   * `mapPosition === 'right'`, or below the address grid in `'bottom'`
   * mode.
   */
  tagsSlot?: React.ReactNode;
  /**
   * View-only mode. When true, every form input is rendered with
   * `disabled` (slate-50 background, no caret), the Places search bar
   * + verification footer are hidden (their only purpose is mutating
   * the address), and the embedded map's "Find on map" button is also
   * hidden. The map itself stays interactive so operators can still
   * pan / zoom to inspect the location. Used by the Locations grid to
   * preview party-linked addresses without letting the operator edit
   * fields they don't own (those are managed on the Party Details page).
   */
  readOnly?: boolean;
  /** Controlled open state. */
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * When `false`, hide the read-only summary line + pencil and render
   * the form panel directly (always open). Useful when this component
   * sits inside a parent's create / edit panel that already provides
   * its own action buttons.
   */
  showSummaryInput?: boolean;
  /** Hide the InlineEditor's footer (X + Save) — parent provides its own. */
  showFooter?: boolean;
  /** Optional remove handler shown bottom-left of the expanded panel. */
  onDelete?: () => void;
  className?: string;
}

/**
 * Address editor lifted from My.Tms.Old/components/AddressInlineEditor.tsx
 * (plain branch — Google Maps integration is a separate component for
 * locations/sites and ports across in a later phase). Built on top of the
 * generic `<InlineEditor>` so the summary-line + expand-panel scaffold is
 * shared with `<RegionalSettingsInlineEditor>` and friends.
 */
export function AddressInlineEditor<T extends AddressInlineEditorValue>({
  label = 'Address',
  value,
  onChange,
  countryOptions,
  hasNameField = false,
  hasCodeField = false,
  enablePlacesSearch = false,
  showMap = false,
  mapPosition = 'bottom',
  tagsSlot,
  readOnly = false,
  isOpen,
  onOpenChange,
  showSummaryInput = true,
  showFooter = true,
  onDelete,
  className,
}: AddressInlineEditorProps<T>) {
  // Summary line shown on the collapsed inline editor. With FullAddress
  // already carrying the comma-separated Google line, the summary is
  // just that string — no field stitching needed. Falls back to City /
  // Postcode / Country when FullAddress is somehow empty (legacy rows
  // mid-migration).
  const summary = useMemo(() => {
    if (value.fullAddress?.trim()) return value.fullAddress;
    return [value.city, value.postcode, value.countryIsoCode]
      .filter(Boolean)
      .join(', ');
  }, [value.fullAddress, value.city, value.postcode, value.countryIsoCode]);

  const setField = <K extends keyof AddressInlineEditorValue>(
    key: K,
    next: AddressInlineEditorValue[K],
  ) => {
    onChange({ ...value, [key]: next } as T);
  };

  // Mirrors the formatted_address back into the Places search box —
  // shared by both entry paths (forward Places pick + reverse "Find
  // on map" geocode) so a successful match always shows up at the
  // top of the editor regardless of which way the operator arrived.
  // Controlled-mode bridge for PlacesAutocomplete (see its
  // `value`/`onValueChange` props).
  const [searchInputValue, setSearchInputValue] = useState('');

  /**
   * Merge a Places / Geocoder result back into the form. Manual edits
   * to any field below clear `latitude`/`longitude`, so picking a
   * fresh place here both repopulates the address fields *and* re-
   * verifies the location in one shot. Also pushes the Google-
   * formatted line up into the search box so the operator can see
   * what Google actually resolved to.
   */
  const onPlacePicked = (place: PlacesAutocompleteResult) => {
    onChange({
      ...value,
      fullAddress: place.formattedAddress,
      city: place.city,
      postcode: place.postcode,
      countryIsoCode: place.countryIsoCode || value.countryIsoCode,
      latitude: place.latitude,
      longitude: place.longitude,
      placeId: place.placeId,
      coordinatesSource: 'Google',
    } as T);
    setSearchInputValue(place.formattedAddress);
  };

  /**
   * Pin repositioned on the map (drag or tap-to-place). Keep the typed address
   * verbatim — only the exact coordinates change, so operators can drop the pin
   * on the actual gate / dock without losing the address text.
   */
  const onCoordsChanged = (latitude: number, longitude: number) => {
    // A pin the operator placed is our data, not Google's: kept as it is.
    onChange({ ...value, latitude, longitude, coordinatesSource: 'Manual' } as T);
  };

  const isVerified = value.latitude != null && value.longitude != null;
  // Heuristic for "match is too broad to be a specific dwelling":
  // Google returned a result but with no street-level identifier
  // (e.g. a postcode-only search lands on the postcode centroid, not
  // a house). With the FullAddress model the signal is whether the
  // Places parser found a street-level component — proxied by
  // `place.address1` being empty when it was first picked. We approx
  // here by checking whether the formatted address starts with the
  // city (a postcode-area match typically returns "Dungiven,
  // Londonderry BT47 4ND, UK" — no house number leading the line).
  const isBroadMatch =
    isVerified &&
    value.fullAddress.length > 0 &&
    !!value.city &&
    value.fullAddress.trim().toLowerCase().startsWith(value.city.toLowerCase());

  // Form column — everything except the map. Pulled into a fragment so
  // the JSX below can render it in either a single-column flow (bottom
  // map) or as the left half of a 2-col grid (right map).
  const formBlock = (
    <>
      {/* Places search + verification footer are mutation-only UI —
          they exist to populate / overwrite the address fields. In
          read-only mode the whole block is skipped (the map below
          still shows the verified location, and the disabled inputs
          show the address). */}
      {enablePlacesSearch && !readOnly && (
        <div className="space-y-1">
          {/*
            Intentionally unrestricted by country — a user's company office
            may sit in a neighbouring country to the one they typically
            operate in (cross-border haulage, etc.), and forcing the picker
            to the form's `countryIsoCode` rules out perfectly valid hits.

            Controlled-mode `value` + `onValueChange` lets us push the
            formatted_address from the "Find on map" reverse-geocode path
            into the search box (which would otherwise stay empty when
            the operator skipped the forward search).
          */}
          <PlacesAutocomplete
            value={searchInputValue}
            onValueChange={setSearchInputValue}
            onPlaceSelected={onPlacePicked}
            placeholder="Search for an address to verify location…"
          />
          <p
            className={
              isBroadMatch
                ? 'text-[11px] font-medium text-amber-700'
                : isVerified
                  ? 'text-[11px] font-medium text-emerald-700'
                  : 'text-[11px] font-medium text-amber-700'
            }
            aria-live="polite"
          >
            {isBroadMatch
              ? `Postcode-area match · ${value.latitude!.toFixed(5)}, ${value.longitude!.toFixed(5)} — add a street name or house number and re-verify for a specific address.`
              : isVerified
                ? `Location verified · ${value.latitude!.toFixed(5)}, ${value.longitude!.toFixed(5)}`
                : 'Location not verified — search above to add coordinates.'}
          </p>
        </div>
      )}
      {hasCodeField && (
        <TextInput
          label="Code"
          name="code"
          autoComplete="off"
          value={value.code ?? ''}
          onChange={(e) => setField('code', e.target.value)}
          placeholder="e.g. HQ, WAREHOUSE-01"
          disabled={readOnly}
        />
      )}
      {hasNameField && (
        <TextInput
          label={requiredLabel('Name')}
          name="name"
          autoComplete="off"
          value={value.name ?? ''}
          onChange={(e) => setField('name', e.target.value)}
          placeholder="e.g. Head office"
          disabled={readOnly}
        />
      )}
      {/* Full address sits above the 2-col grid so it can span the
          full width — Google-formatted lines like "Stratum Unit
          B8/B9, Ballymount Corporate Park, Ballymount Ave,
          Ballymount Rd, Dublin 12, D12 ER82, IE" don't fit
          comfortably in a half-row column. The City / Postcode /
          Country sidecars sit below in 2 cols as searchable
          metadata. Operators normally fill all four via the Places
          picker; manual edits are allowed but typically rare. */}
      <TextInput
        label={requiredLabel('Full address')}
        name="fullAddress"
        autoComplete="off"
        value={value.fullAddress}
        onChange={(e) => setField('fullAddress', e.target.value)}
        disabled={readOnly}
        helperText="Verified by Google Places — pick from the search box above or use Find on map below to populate."
      />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <TextInput
          label="City"
          name="city"
          autoComplete="off"
          value={value.city ?? ''}
          onChange={(e) => setField('city', e.target.value)}
          disabled={readOnly}
        />
        <TextInput
          label="Postcode"
          name="postcode"
          autoComplete="off"
          value={value.postcode ?? ''}
          onChange={(e) => setField('postcode', e.target.value)}
          disabled={readOnly}
        />
        {countryOptions ? (
          <SelectInput
            label={requiredLabel('Country')}
            name="countryIsoCode"
            value={value.countryIsoCode}
            onChange={(e) => setField('countryIsoCode', e.target.value)}
            options={countryOptions}
            disabled={readOnly}
          />
        ) : (
          <TextInput
            label={requiredLabel('Country (ISO)')}
            name="countryIsoCode"
            autoComplete="off"
            value={value.countryIsoCode}
            onChange={(e) => setField('countryIsoCode', e.target.value.toUpperCase())}
            helperText="2-letter ISO code, e.g. IE, GB."
            disabled={readOnly}
          />
        )}
      </div>
      {tagsSlot}
    </>
  );

  // Split-pane mode. The grid stacks to a single column on mobile so the
  // map doesn't squash the form into a 50% slice of a phone screen, and
  // the map's `fillHeight` flag makes it stretch to match the form
  // column height on `md+` rather than sticking at the fixed 260px used
  // in `'bottom'` mode.
  const useSplitPane = showMap && mapPosition === 'right';

  return (
    <InlineEditor
      label={label}
      summary={summary}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      showSummaryInput={showSummaryInput}
      showFooter={showFooter}
      onDelete={onDelete}
      ariaSubject={label}
      className={className}
    >
      {useSplitPane ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-3">{formBlock}</div>
          <div className="md:min-h-[420px]">
            <AddressMapPanel
              value={value}
              onPlaceMatched={onPlacePicked}
              onCoordsChanged={onCoordsChanged}
              fillHeight
              readOnly={readOnly}
            />
          </div>
        </div>
      ) : (
        <>
          {formBlock}
          {showMap && (
            <AddressMapPanel
              value={value}
              onPlaceMatched={onPlacePicked}
              onCoordsChanged={onCoordsChanged}
              readOnly={readOnly}
            />
          )}
        </>
      )}
    </InlineEditor>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Map panel — "Find on map" geocode button + embedded Google Map with a
// marker + accuracy circle when coordinates are verified. Lifted from the
// legacy My.Tms.Old AddressInlineEditor's `AddressFormWithMap` flow but
// trimmed of the Places search (the parent already renders that above).
//
// Uses the simpler `<Marker>` / `google.maps.Circle` rather than
// `AdvancedMarkerElement`, so no `mapId` / cloud-style registration is
// required — works against any provider that's already loaded.
// ────────────────────────────────────────────────────────────────────────────

interface AddressMapPanelProps {
  value: AddressInlineEditorValue;
  /**
   * Fired when "Find on map" successfully reverse-geocodes the
   * current form fields into a Google match. Carries the full
   * parsed result — same shape as
   * <see cref="PlacesAutocompleteResult"/> — so the parent can
   * merge structured fields (address1, city, postcode, etc.) AND
   * coordinates in one shot. Without this, typing just a postcode +
   * clicking Find on map would only update the pin and leave the
   * structured fields stale.
   */
  onPlaceMatched: (result: PlacesAutocompleteResult) => void;
  /**
   * When true, the panel becomes a flex column and the map div grows
   * to fill the parent's height (with a 260px floor so it doesn't
   * collapse). Used by the split-pane layout where the map sits in a
   * grid cell whose height matches the form column. In the default
   * `'bottom'` layout the map sticks at a fixed 260px.
   */
  fillHeight?: boolean;
  /**
   * View-only mode. Hides the "Find on map" geocode button (its only
   * purpose is mutating coordinates) but keeps the embedded map and
   * the read-out of current coords visible — operators can still pan
   * / zoom to inspect the location.
   */
  readOnly?: boolean;
  /**
   * Fired when the operator repositions the pin (drag or tap-to-place). Carries
   * the new coordinates only, so the parent updates lat/lng and keeps the typed
   * address as-is.
   */
  onCoordsChanged: (latitude: number, longitude: number) => void;
}

function AddressMapPanel({
  value,
  onPlaceMatched,
  onCoordsChanged,
  fillHeight = false,
  readOnly = false,
}: AddressMapPanelProps) {
  // Each instance gets its own Map id so multiple AddressInlineEditors on
  // the same page (Primary + Billing on Parties) don't share state.
  const mapIdRef = useRef(`address-map-${Math.random().toString(36).slice(2, 10)}`);
  const map = useMap(mapIdRef.current);
  const geocodingLib = useMapsLibrary('geocoding');
  const [geocoding, setGeocoding] = useState(false);

  const hasCoords =
    value.latitude != null &&
    value.longitude != null &&
    !Number.isNaN(value.latitude) &&
    !Number.isNaN(value.longitude);

  // IE-centred default — sensible for the suite's primary market. The
  // map immediately pans to verified coords once they exist, so this only
  // shows as the initial state when the address hasn't been geocoded yet.
  const center = hasCoords
    ? { lat: value.latitude as number, lng: value.longitude as number }
    : { lat: 53.35, lng: -6.26 };

  // Maintain a marker + 50m accuracy circle imperatively. The package's
  // declarative `<Marker>` would also work but the imperative pan/zoom
  // here gives us a tighter "snap to verified location" UX.
  const markerRef = useRef<google.maps.Marker | null>(null);
  const circleRef = useRef<google.maps.Circle | null>(null);
  // Latest onCoordsChanged, read through a ref so the map listeners always call
  // the current callback without having to re-subscribe on every render.
  const onCoordsRef = useRef(onCoordsChanged);
  onCoordsRef.current = onCoordsChanged;
  // Set right before a user-driven coord change (pin drag / map tap) so the
  // effect below skips its auto pan/zoom/fit — the camera must not jump out
  // from under the operator while they position the pin.
  const suppressAutoFrameRef = useRef(false);

  useEffect(() => {
    if (!map) return;

    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }

    if (hasCoords) {
      // Draggable when editable — drop the pin exactly on the gate / dock.
      // A drag updates only the coordinates (typed address is kept) and sets
      // the suppress flag so the auto-frame below leaves the camera put.
      markerRef.current = new google.maps.Marker({
        map,
        position: center,
        draggable: !readOnly,
      });
      if (!readOnly) {
        markerRef.current.addListener('dragend', (e: google.maps.MapMouseEvent) => {
          const lat = e.latLng?.lat();
          const lng = e.latLng?.lng();
          if (lat != null && lng != null) {
            suppressAutoFrameRef.current = true;
            onCoordsRef.current(lat, lng);
          }
        });
      }
      circleRef.current = new google.maps.Circle({
        map,
        center,
        radius: 50,
        fillColor: '#3b82f6',
        fillOpacity: 0.15,
        strokeColor: '#3b82f6',
        strokeOpacity: 0.4,
        strokeWeight: 1.5,
      });
      // Auto-frame only on a geocode / initial load — never on a user drag/tap.
      if (!suppressAutoFrameRef.current) {
        map.panTo(center);
        map.setZoom(17);
        const bounds = circleRef.current.getBounds();
        if (bounds) map.fitBounds(bounds);
      }
    } else {
      map.panTo({ lat: 53.35, lng: -6.26 });
      map.setZoom(5);
    }
    suppressAutoFrameRef.current = false;

    return () => {
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
    };
  }, [map, hasCoords, value.latitude, value.longitude, readOnly]);

  // Tap-to-place: click anywhere on the map to move the pin there (editable
  // only). Attached once per map; calls the latest callback via the ref.
  useEffect(() => {
    if (!map || readOnly) return;
    const listener = map.addListener('click', (e: google.maps.MapMouseEvent) => {
      const lat = e.latLng?.lat();
      const lng = e.latLng?.lng();
      if (lat != null && lng != null) {
        suppressAutoFrameRef.current = true;
        onCoordsRef.current(lat, lng);
      }
    });
    return () => listener.remove();
  }, [map, readOnly]);

  // Build a one-line address string for the geocoder. Prefers the
  // already-formatted FullAddress when available (it's the cleanest
  // representation Google's geocoder will recognise round-trip);
  // falls back to the sidecar fields when FullAddress is empty
  // (e.g. operator only typed a postcode + clicked Find on map).
  const buildAddressString = useCallback(() => {
    if (value.fullAddress?.trim()) return value.fullAddress.trim();
    return [value.city, value.postcode, value.countryIsoCode]
      .filter(Boolean)
      .join(', ');
  }, [value.fullAddress, value.city, value.postcode, value.countryIsoCode]);

  const handleGeocode = async () => {
    if (!geocodingLib) return;
    const address = buildAddressString();
    if (!address.trim()) return;
    setGeocoding(true);
    try {
      const geocoder = new geocodingLib.Geocoder();
      const response = await geocoder.geocode({ address });
      const first = response.results?.[0];
      if (first) {
        // Parse the full Google result — same helper the forward
        // (Places) search uses — so structured fields, country,
        // and coordinates all come back populated. Without this,
        // typing just a postcode + clicking Find on map would
        // only update the pin and leave Address Line 1 / City /
        // etc. either empty (new address) or stale (existing
        // address re-verified after the postcode changed).
        onPlaceMatched(extractAddressFromGoogleResult(first));
      }
    } catch (err) {
      // Non-fatal — operator can retry, or pick from PlacesAutocomplete
      // above. The "not verified" amber line stays as the visual signal.
      // eslint-disable-next-line no-console
      console.error('Geocoding failed', err);
    } finally {
      setGeocoding(false);
    }
  };

  const addressString = buildAddressString();

  return (
    <div className={fillHeight ? 'flex h-full flex-col space-y-2' : 'space-y-2'}>
      <div className="flex flex-wrap items-center gap-2">
        {/* "Find on map" mutates the verified coordinates — hidden in
            read-only mode (the form fields are also disabled there).
            The coords readout below stays so operators still see the
            verification status. */}
        {!readOnly && (
          <button
            type="button"
            onClick={handleGeocode}
            disabled={geocoding || !addressString.trim() || !geocodingLib}
            className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50"
          >
            <MapPin className="h-3.5 w-3.5" />
            {geocoding ? 'Locating…' : 'Find on map'}
          </button>
        )}
        {hasCoords && (
          <span className="text-[10px] text-slate-500">
            Coordinates: {value.latitude!.toFixed(5)}, {value.longitude!.toFixed(5)}
            {value.coordinatesSource === 'Manual' && (
              <span className="ml-1 text-emerald-700" title="Placed on the map by hand — kept as it is">
                · pin set by hand
              </span>
            )}
          </span>
        )}
      </div>
      {!readOnly && hasCoords && (
        <p className="text-[10px] text-slate-400">
          Drag the pin — or tap the map — to set the exact point.
        </p>
      )}
      <div
        className={
          fillHeight
            ? 'min-h-[260px] w-full flex-1 overflow-hidden rounded-md border border-slate-200'
            : 'h-[260px] w-full overflow-hidden rounded-md border border-slate-200'
        }
      >
        <Map
          id={mapIdRef.current}
          defaultCenter={center}
          defaultZoom={hasCoords ? 15 : 5}
          {...MAP_DEFAULT_PROPS}
        />
      </div>
    </div>
  );
}

/**
 * Floating-label content with a trailing red asterisk for the required
 * fields (Name when present, Address Line 1, Country). Postcode + City +
 * Address Line 2 are optional at the BE schema level so they're left
 * unmarked here.
 */
function requiredLabel(text: string) {
  return (
    <>
      {text} <span className="text-red-500">*</span>
    </>
  );
}
